import clsx from 'clsx';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import Tooltip from '@material-ui/core/Tooltip';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Brightness2Icon from '@material-ui/icons/Brightness2';
import WbSunnyIcon from '@material-ui/icons/WbSunny';
import { sidebarConfig } from '@backstage/core-components';
import { useThemeToggle } from './useThemeToggle';
import { MoreThemesTooltip } from './MoreThemesTooltip';

// Toggle dimensions for collapsed mode
const TOGGLE_WIDTH = 40;
const TOGGLE_HEIGHT = 28;
const THUMB_SIZE = 24;
const THUMB_OFFSET = 2;

const useStyles = makeStyles<Theme>(theme => ({
  listItem: {
    height: 48,
    padding: 0,
    paddingRight: theme.spacing(3),
    marginBottom: 8,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor:
        theme.palette.navigation.navItem?.hoverBackground ?? '#404040',
    },
  },
  iconContainer: {
    boxSizing: 'border-box',
    width: sidebarConfig.drawerWidthClosed,
    minWidth: sidebarConfig.drawerWidthClosed,
    marginRight: -theme.spacing(2),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  toggle: {
    position: 'relative',
    width: TOGGLE_WIDTH,
    height: TOGGLE_HEIGHT,
    borderRadius: TOGGLE_HEIGHT / 2,
    backgroundColor:
      theme.palette.navigation.navItem?.hoverBackground ?? '#404040',
    transition: 'background-color 200ms ease',
    flexShrink: 0,
  },
  thumb: {
    position: 'absolute',
    top: THUMB_OFFSET,
    width: THUMB_SIZE,
    height: THUMB_SIZE,
    borderRadius: '50%',
    backgroundColor: theme.palette.navigation.background,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'left 200ms ease',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
  },
  thumbDark: {
    left: THUMB_OFFSET,
  },
  thumbLight: {
    left: TOGGLE_WIDTH - THUMB_SIZE - THUMB_OFFSET,
    // In light mode, when light theme is selected, thumb should be white
    backgroundColor:
      theme.palette.type === 'dark'
        ? theme.palette.navigation.background
        : '#ffffff',
  },
  moonIconSmall: {
    fontSize: 16,
    color: theme.palette.navigation.color,
  },
  sunIconSmall: {
    fontSize: 16,
    color: '#F5A623',
  },
  tooltipTextCenter: {
    textAlign: 'center',
  },
}));

export const CollapsedThemeToggle = () => {
  const classes = useStyles();
  const {
    isDark,
    setThemeVariant,
    showExtendedTooltip,
    handleMouseEnter,
    handleMouseLeave,
    handlePopperMouseEnter,
    handlePopperMouseLeave,
    anchorRef,
  } = useThemeToggle();

  const handleToggle = () => {
    setThemeVariant(isDark ? 'light' : 'dark');
  };

  const tooltipText = (
    <div className={classes.tooltipTextCenter}>
      Toggle {isDark ? 'light' : 'dark'}
      <br />
      mode
    </div>
  );

  return (
    <div
      ref={anchorRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Tooltip
        title={tooltipText}
        placement="top"
        PopperProps={{
          style: { zIndex: 1600 },
        }}
      >
        <ListItem button className={classes.listItem} onClick={handleToggle}>
          <ListItemIcon className={classes.iconContainer}>
            <div className={classes.toggle}>
              <div
                className={clsx(
                  classes.thumb,
                  isDark ? classes.thumbDark : classes.thumbLight,
                )}
              >
                {isDark ? (
                  <Brightness2Icon
                    className={classes.moonIconSmall}
                    data-testid="Brightness2Icon"
                  />
                ) : (
                  <WbSunnyIcon
                    className={classes.sunIconSmall}
                    data-testid="WbSunnyIcon"
                  />
                )}
              </div>
            </div>
          </ListItemIcon>
        </ListItem>
      </Tooltip>

      <MoreThemesTooltip
        open={showExtendedTooltip}
        anchorEl={anchorRef.current}
        onMouseEnter={handlePopperMouseEnter}
        onMouseLeave={handlePopperMouseLeave}
      />
    </div>
  );
};
